import SearchInput from "../Search";
import Tree from "../../common/Svg/Tree";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCustomerGroups } from "../../../services/customerGroups/customerGroups.service";
import { setCustomersList } from "../../../store/customerList.store";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../../store/toasterslice";

interface CustomerGroupsProps {
  toggleSidebar: (isOpen: boolean | null, selectedMenu: string | null) => void;
}

type UiItem = {
  id: string;
  groupName: string;
  showAssets: boolean;
  childGroups: any[];
  totalTankCount: number;
};

const CustomerGroups: React.FC<CustomerGroupsProps> = ({ toggleSidebar }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState<UiItem[]>([]);
  const [filterText, setFilterText] = useState<string>("");
  const [filterGroupData, setFilterGroupData] = useState<UiItem[]>([]);
  const navigate = useNavigate();
  const { customerId } = useParams();

  useEffect(() => {
    gettingAllCustomerGroups(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gettingAllCustomerGroups = async (shouldUpdateStore = false) => {
    try {
      const response: any = await getCustomerGroups();
      if (response?.statusCode === 200 && response?.success) {
        if (shouldUpdateStore) {
          dispatch(setCustomersList(response.data.customerlst));
        }

        const raw = response.data.customerlst ?? [];

        // Filter only valid entries with customerId and totalDevice
        const validEntries = raw.filter(
          (item: any) =>
            item?.customerId !== undefined &&
            item?.totalDevice !== undefined &&
            item?.customerId !== null &&
            item?.customerId !== "",
        );

        // Calculate 'All Customers' total from valid entries excluding unassigned
        const allCustomersCount = validEntries.reduce(
          (acc: number, item: { totalDevice: any }) =>
            acc + Number(item.totalDevice ?? 0),
          0,
        );
        // Extract Unassigned separately
        const unassignedItem = validEntries.find(
          (item: { customerId: string }) => item.customerId === "-1",
        );
        const unassignedCount = Number(unassignedItem?.totalDevice ?? 0);

        // Prepare Assigned Customer List
        const assignedCustomers: UiItem[] = validEntries
          .filter((item: { customerId: string }) => item.customerId !== "-1")
          .map(
            (item: {
              customerId: any;
              customerName: any;
              totalDevice: any;
            }) => ({
              id: item.customerId,
              groupName: item.customerName,
              showAssets: false,
              childGroups: [],
              totalTankCount: Number(item.totalDevice ?? 0),
            }),
          )
          .sort((a: { groupName: string }, b: { groupName: any }) =>
            a.groupName.localeCompare(b.groupName),
          );

        // Final customer group list
        const items: UiItem[] = [
          {
            id: "allCustomers",
            groupName: "All Customers",
            showAssets: false,
            childGroups: [],
            totalTankCount: allCustomersCount,
          },
          ...assignedCustomers,
        ];

        if (unassignedCount > 0) {
          items.push({
            id: "-1",
            groupName: "Unassigned",
            showAssets: false,
            childGroups: [],
            totalTankCount: unassignedCount,
          });
        }

        const filtered = filterDataByName(items, "");
        setFilterGroupData(filtered);
        setData(items);
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          message: "Failed to load customer groups",
          type: "error",
          visible: true,
        }),
      );
      setFilterGroupData([]);
      setData([]);
    }
  };

  const filterDataByName = (items: UiItem[], searchText: string) => {
    if (!searchText) {
      return items
        .map((item) => ({ ...item, showAssets: false }))
        .filter((item) => item.totalTankCount > 0);
    }

    return items
      .map((item) => {
        const match =
          item?.groupName?.toLowerCase().includes(searchText.toLowerCase()) &&
          item.totalTankCount > 0;

        const filteredItem: UiItem = {
          ...item,
          showAssets: true,
          childGroups: item.childGroups
            ? filterDataByName(item.childGroups, searchText)
            : [],
        };

        if (
          match ||
          (filteredItem.childGroups && filteredItem.childGroups.length > 0)
        ) {
          return filteredItem;
        }
        return null as any;
      })
      .filter(Boolean);
  };

  const handleInputSearch = (e: any) => {
    const val = e?.target?.value || "";
    setFilterText(val);
    setFilterGroupData(filterDataByName(data, val));
  };

  const handleCustomerGroupClick = (customerItem: UiItem) => {
    if (customerId !== customerItem?.id) {
      toggleSidebar(false, "customerGroupsMenu");
      navigate(
        `/${
          customerItem.id === "allCustomers"
            ? ""
            : customerItem.id === "-1"
              ? "-1"
              : customerItem.id
        }`,
        { replace: true },
      );
    } else {
      toggleSidebar(false, "customerGroupsMenu");
    }
  };

  return (
    <>
      <SearchInput
        searchValue={filterText}
        onSearchChange={handleInputSearch}
        placeholder="Search by customer or group name"
      />

      <div className="flex flex-col max-h-full h-[85vh] overflow-y-auto pt-[20px]">
        <h6 className="text-[17px] font-[500] text-primary-400 mb-2">
          Customers & Groups
        </h6>

        {filterGroupData.map((itemCustomer: UiItem, index: number) => {
          const isSelected = customerId
            ? customerId === itemCustomer.id
            : index === 0;
          return (
            <div key={itemCustomer.id} className="w-full">
              <div
                className={`flex justify-between items-center w-full px-[12px] py-[8px] mb-[4px] font-Mulish ${
                  isSelected ? "bg-darkcolor-600 rounded-[6px]" : ""
                }`}
              >
                <div className="flex items-center gap-[12px]">
                  <Tree color={isSelected ? "#A6DBEA" : "#F7F9F9"} />
                  <label
                    className={`text-[16px] cursor-pointer ${
                      isSelected ? "text-[#A6DBEA]" : "text-neutral-25"
                    }`}
                    onClick={() => handleCustomerGroupClick(itemCustomer)}
                  >
                    {itemCustomer.groupName}
                  </label>
                </div>
                <div className="flex items-center gap-[12px]">
                  <div className="flex items-center justify-center rounded-[4px] bg-primary-300 px-[10px] py-[2px]">
                    {itemCustomer.totalTankCount}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CustomerGroups;
